import { ViewUserResource, usePatchCustomerUsersByUserMutation } from '@/api/serverApi'
import { InputLabel, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import Button from '@cfs-fixings/shared/src/components/button'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

const textSpec = {
  'nameField': {
    'label': 'Name',
    'placeholder': 'Name',
  },
  'emailField': {
    'label': 'Email',
    'placeholder': 'Email',
  },
  'cancelButtonLabel': 'Cancel',
  'saveButtonLabel': 'Save Changes',
  'successToast': {
    'title': 'User updated',
    'description': 'User has been successfully updated',
  },
  'errorToast': {
    'title': 'Failed to update user',
    'description': 'An error occurred while updating the user',
  },
}

type PropTypes = {
    userModel: ViewUserResource
    onUserHasChanges?: (hasChanges: boolean) => void
    onCancel?: () => void
    onSuccess?: () => void
}

const EditTeamMemberForm = ({ userModel, onCancel, onSuccess, onUserHasChanges }: PropTypes) => {

  const [update, { isLoading }] = usePatchCustomerUsersByUserMutation()

  const { register, formState: { errors, dirtyFields }, setError, handleSubmit } = useForm({
    defaultValues: {
      'name': userModel?.name,
      'email': userModel?.email,
      'customer_id': userModel?.customer.id,
    },
  })

  const errorHandler = useErrorHandler()

  const onSubmit = (data: any) => {
    update({ user: userModel!.id, body: data }).unwrap().then(() => {
      onSuccess?.()
      toastHelper.success(textSpec.successToast.title, textSpec.successToast.description)
    }).catch((error) => {
      if (error.status !== 422) {
        toastHelper.error(textSpec.errorToast.title, textSpec.errorToast.description)
      } else {
        errorHandler(error, setError)
      }
    })
  }

  useEffect(() => {
    const hasChanges = dirtyFields && Object.keys(dirtyFields).length > 0
    onUserHasChanges?.(hasChanges)
  }, [dirtyFields, onUserHasChanges])

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col gap-10 mt-8">

        {/* Name and Email */}
        <div className="flex flex-col gap-[30px]">
          <div>
            <InputLabel>{textSpec.nameField.label}</InputLabel>
            <TextInput
              placeholder={textSpec.nameField.placeholder}
              error={errors?.name?.message as string}
              {...register('name')}
            />
          </div>
          <div>
            <InputLabel>{textSpec.emailField.label}</InputLabel>
            <TextInput
              placeholder={textSpec.emailField.placeholder}
              error={errors?.email?.message as string}
              {...register('email')}
            />
          </div>
        </div>

        {/* Delete, cancel, and save buttons */}
        <div className="flex gap-4">
          <Button
            variant="secondary"
            size="lg"
            onClick={onCancel}
            isLoading={isLoading}
          >
            {textSpec.cancelButtonLabel}
          </Button>
          <Button
            type="submit"
            variant="dark"
            size="lg"
            isLoading={isLoading}
          >
            {textSpec.saveButtonLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default EditTeamMemberForm
